import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { BeginTotpSetupDto, CompleteMfaSetupResult, CompleteMfaVerificationResult, ConfirmTotpSetupResult, MfaSettingsDto, MfaVerificationResult, VerifyMfaCodeResult } from "../models/mfa.models";

@Injectable({ providedIn: "root" })
export class MfaService {
    private readonly base = "/api/Mfa";

    public constructor(private readonly http: HttpClient) {}

    public getSettings(): Observable<MfaSettingsDto> {
        return this.http.get<
            { twoFactorEnabled: boolean; hasSecret?: boolean; mustEnableTwoFactor?: boolean; canDisable?: boolean; }
        >(`${this.base}/GetMfaSettings`).pipe(map((r): MfaSettingsDto => ({
            isEnabled: r.twoFactorEnabled,
            hasSecret: r.hasSecret,
            mustEnableTwoFactor: r.mustEnableTwoFactor,
            canDisable: r.canDisable,
        })));
    }

    public beginSetup(): Observable<BeginTotpSetupDto> {
        return this.http.post<BeginTotpSetupDto>(`${this.base}/BeginTotpSetup`, {});
    }

    public beginSetupForUser(userId: string): Observable<BeginTotpSetupDto> {
        return this.http.post<BeginTotpSetupDto>(`${this.base}/BeginTotpSetup`, { userId });
    }

    public confirmSetup(code: string): Observable<MfaVerificationResult> {
        return this.http.post<MfaVerificationResult>(`${this.base}/ConfirmTotpSetup`, { code }).pipe(
            catchError((e) =>
                of({ success: false, twoFactorEnabled: false, errorCode: e?.error?.errorCode } as MfaVerificationResult)
            ),
        );
    }

    public confirmSetupForUser(code: string, userId: string): Observable<ConfirmTotpSetupResult> {
        return this.http.post<ConfirmTotpSetupResult>(`${this.base}/ConfirmTotpSetup`, { code, userId }).pipe(
            catchError((e) =>
                of({
                    twoFactorEnabled: false,
                    autoLoginRequired: false,
                    errorCode: e?.error?.errorCode,
                } as ConfirmTotpSetupResult)
            ),
        );
    }

    public completeMfaSetup(userId: string, twoFactorCode: string): Observable<CompleteMfaSetupResult> {
        return this.http.post<CompleteMfaSetupResult>("/api/Account/CompleteMfaSetup", {
            userId,
            twoFactorCode,
        });
    }

    public verifyMfaCode(userId: string, code: string): Observable<VerifyMfaCodeResult> {
        return this.http.post<VerifyMfaCodeResult>(`${this.base}/VerifyMfaCode`, {
            userId,
            code,
        }).pipe(
            catchError((e) =>
                of({ success: false, autoLoginRequired: false, errorCode: e?.error?.errorCode } as VerifyMfaCodeResult)
            ),
        );
    }

    public completeMfaVerification(userId: string): Observable<CompleteMfaVerificationResult> {
        return this.http.post<CompleteMfaVerificationResult>("/api/Account/CompleteMfaVerification", {
            userId,
        });
    }

    public disable(): Observable<MfaVerificationResult> {
        return this.http.post<MfaVerificationResult>(`${this.base}/DisableTwoFactor`, {});
    }
}
